import { createContext, type ReactNode, type RefObject, useContext, useEffect, useRef, useState } from "react";
import { api, type Tip } from "./api.ts";
import { type ExampleAsk, examplePrompt } from "./ask-ai.ts";
import { useReadOnly } from "./readOnly.ts";
import { Button } from "./souvenir.ts";
import { CopyButton, PromptWords } from "./ui.tsx";

// Help just in time, inside the trip (features/getting-started.md, "Just in time, inside the trip"):
// the first time someone opens each view, a small card at its top says what the view shows and that
// their AI fills it, with one prompt to copy; Got it puts it away for good, on every device (Tripmo
// keeps it, `tips_seen`). The first time they see the rating buttons, one line says what they're for,
// put away once seen. Tips never stack: one at a time, and none over a card that's open. A read-only trip
// shows none (features/paying.md, "Spots"): each says how your AI fills the view, which it can't there; they
// show again once it can change.

export type ViewTip = Exclude<
  Tip,
  "rating" | "how_tripmo_works" | "skip_connecting" | "connect_to_start" | "first_trip"
>;

// What each view's card says, and the prompt it offers (ask-ai.ts's, naming the trip).
const VIEW_TIPS: Record<ViewTip, { name: string; text: string; ask: ExampleAsk }> = {
  map: {
    name: "the map",
    text: "The map shows every place, stay and journey in the trip, and who wants each. Your AI puts them here as you plan.",
    ask: "map",
  },
  days: {
    name: "Days",
    text: "Days lays the trip out day by day as plans firm up. Plan a place for a day from its card, or your AI fills the days in from what you all want.",
    ask: "days",
  },
  ideas: {
    name: "Ideas",
    text: "Ideas is the places and goals you're considering, with friends' tips and what your AIs found. Your AI adds them; you rate them here.",
    ask: "ideas",
  },
  people: {
    name: "People",
    text: "People shows who's in the trip and what each person wants. It fills in as each of you rates ideas, here or through your AI.",
    ask: "people",
  },
  bookings: {
    name: "Bookings",
    text: "Bookings keeps what's to decide, what needs booking, what's booked, the to-dos and what's been paid. Mark things booked and tick off to-dos here; your AI saves the rest.",
    ask: "bookings",
  },
  updates: {
    name: "Updates",
    text: "Updates shows questions waiting on you, then every change to the trip, newest first. Your AI can catch you up.",
    ask: "updates",
  },
};

// The prompt a view's card offers in a trip: its empty state offers the same.
export const viewPrompt = (view: ViewTip, trip: string) => examplePrompt(trip, VIEW_TIPS[view].ask);

// Put away from this page, while it's open: the person's tips were loaded with them once (`me`), and a
// trip opened again from Your trips mustn't show again what was just put away.
const awayHere = new Set<Tip>();

interface Tips {
  // The trip's name, which the cards' prompts start with.
  trip: string;
  open: (tip: Tip) => boolean;
  putAway: (tip: Tip) => void;
  // How many first-visit cards show now: while one does, the rating line waits.
  cards: number;
  setCards: (change: (n: number) => number) => void;
  // The rating strip showing the line now, if one is.
  rateLine: string | null;
  setRateLine: (owner: string | null) => void;
}

const TipsContext = createContext<Tips | null>(null);

export function TipsProvider({ trip, seen, children }: { trip: string; seen: Tip[]; children: ReactNode }) {
  const [away, setAway] = useState(() => new Set<Tip>([...seen, ...awayHere]));
  const [cards, setCards] = useState(0);
  const [rateLine, setRateLine] = useState<string | null>(null);
  const value: Tips = {
    trip,
    open: (tip) => !away.has(tip),
    putAway: (tip) => {
      if (away.has(tip)) return;
      awayHere.add(tip);
      setAway((prev) => new Set(prev).add(tip));
      // Gone at once; if it doesn't reach Tripmo (no signal), it shows again next time, which is no harm.
      api.putTipAway(tip).catch(() => {});
    },
    cards,
    setCards,
    rateLine,
    setRateLine,
  };
  return <TipsContext.Provider value={value}>{children}</TipsContext.Provider>;
}

// Whether a view's first-visit card is still to show (not put away). While it shows, it stands in for the
// view's own intro line, which said the same just above it; after Got it, the intro is back.
export function useFirstVisitOpen(view: ViewTip): boolean {
  const tips = useContext(TipsContext);
  const readOnly = useReadOnly();
  return !readOnly && tips?.open(view) === true;
}

// A view's first-visit card, at its top, until Got it. `hidden`: not now (a card is open over it, or the
// view explains itself already, empty), without putting it away.
export function FirstVisit({
  view,
  hidden = false,
  className,
}: {
  view: ViewTip;
  hidden?: boolean;
  className?: string;
}) {
  const tips = useContext(TipsContext);
  const open = useFirstVisitOpen(view);
  const shown = tips !== null && !hidden && open;
  const setCards = tips?.setCards;
  useEffect(() => {
    if (!shown || !setCards) return;
    setCards((n) => n + 1);
    return () => setCards((n) => n - 1);
  }, [shown, setCards]);
  if (!shown) return null;
  const tip = VIEW_TIPS[view];
  const prompt = viewPrompt(view, tips.trip);
  return (
    <aside className={`tm-first-visit${className ? ` ${className}` : ""}`} aria-label={`About ${tip.name}`}>
      <p className="callout">{tip.text}</p>
      <PromptWords text={prompt} lead="Ask your AI:" />
      <div className="tm-first-visit-foot">
        <CopyButton text={prompt} />
        <Button variant="primary" size="sm" onClick={() => tips.putAway(view)}>
          Got it
        </Button>
      </div>
    </aside>
  );
}

// The line under the rating buttons the first time someone sees them: on the first strip that comes into
// view (none while a view's first-visit card shows), put away for good as it's seen, and gone from this
// page once that strip goes or they rate something. `id`: the strip's own, `ref`: where it is.
export function useRateLine(id: string, ref: RefObject<HTMLElement | null>): { shown: boolean; rated: () => void } {
  const tips = useContext(TipsContext);
  const free = tips?.open("rating") === true && tips.cards === 0 && tips.rateLine === null;
  const mine = tips !== null && tips.rateLine === id;
  // The latest, for the observer and the clean-up, without observing again on every render.
  const latest = useRef(tips);
  latest.current = tips;
  useEffect(() => {
    const el = ref.current;
    if (!free || !el) return;
    const seen = new IntersectionObserver(
      (entries) => {
        const t = latest.current;
        if (!entries.some((e) => e.isIntersecting) || !t || t.rateLine !== null || !t.open("rating")) return;
        t.setRateLine(id);
        t.putAway("rating");
      },
      { threshold: 0.9 },
    );
    seen.observe(el);
    return () => seen.disconnect();
  }, [free, id, ref]);
  // The strip that showed it goes (its card closes, its view changes): the line goes with it.
  useEffect(() => {
    if (!mine) return;
    return () => latest.current?.setRateLine(null);
  }, [mine]);
  return {
    shown: mine,
    rated: () => {
      if (!tips) return;
      tips.putAway("rating");
      if (tips.rateLine !== null) tips.setRateLine(null);
    },
  };
}
